import type {
  Home,
  MonitoringCellData,
  MonitoringReportRow
} from '@lmc/ts-common'
import { Avatar, Success, WarningIcon } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Popover, Table, type TableColumnGroupType } from 'antd'
import { getNominative } from 'common/utils'
import dayjs from 'dayjs'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { generateDatesArray } from 'screens/Reports/RiskAssessmentDashboard/screens/utils/utils'
import styled from 'styled-components'
import { formatData, generateQueryString, getReportPath } from '../../utils'
import type { FilterValues } from '../MonitoringReport/MonitoringReport'
import MonitoringReportCellPopover from '../MonitoringReportCellPopover/MonitoringReportCellPopover'

type DashboardTableProps = {
  dataSource?: MonitoringReportRow[]
  filterParams: FilterValues
  reportingId: string
}

const DashboardTable = ({
  dataSource,
  filterParams,
  reportingId
}: DashboardTableProps) => {
  const datesArray = generateDatesArray(
    filterParams.rangePicker?.[0] || dayjs(),
    filterParams.rangePicker?.[1] || dayjs()
  )
  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const peopleNominative = getNominative(preferredPeopleNominative, 'Residents')

  const flags = useFeatureFlag({
    flagNames: ['fluid_tracking_monitoring_report']
  })?.data

  const fluidTrackingMonitoringReportEnabled =
    flags?.fluid_tracking_monitoring_report?.enabled

  const columns: TableColumnGroupType<MonitoringReportRow>[] = [
    {
      title: ' ',
      children: [
        {
          dataIndex: 'resident',
          title: (
            <SpacedColumnHeader>
              <BaseText weight="medium">{peopleNominative}</BaseText>
            </SpacedColumnHeader>
          ),
          key: 'resident',
          fixed: 'left',
          sorter: (a, b) =>
            a.resident.name?.first?.localeCompare(b.resident?.name?.first),
          width: 140,
          defaultSortOrder: 'ascend',
          render: (row: MonitoringReportRow['resident']) => (
            <Link
              rel="noopener noreferrer"
              target="_blank"
              to={`/residents/${row.id}/charts/${getReportPath(reportingId)}`}
            >
              <PersonCard>
                <Avatar url={row.picture} size={30} />
                <BaseText weight="bold">
                  {row.name?.first} {row.name?.last}
                </BaseText>
              </PersonCard>
            </Link>
          )
        }
      ]
    },
    ...datesArray.map(({ month, days }) => ({
      title: (
        <YearContainer>
          <YearText level={4} weight="regular">
            {dayjs(month, 'MM/YY').format('MMMM YYYY')}
          </YearText>
        </YearContainer>
      ),
      children: days.map((date) => ({
        dataIndex: 'data',
        title: (
          <DateContainer>
            <BaseText weight="medium">
              {dayjs(date, 'DD/MM/YYYY').format('D')}
            </BaseText>
            <DayText weight="regular">
              {dayjs(date, 'DD/MM/YYYY').format('ddd')}
            </DayText>
          </DateContainer>
        ),
        key: 'data',
        render: (row: MonitoringCellData[], record: MonitoringReportRow) => {
          const cellData = row.find((data) => data.date === date)

          const formattedCellData = cellData?.value
            ? formatData(cellData.value)
            : ''

          const getCellValue = () => {
            if (
              fluidTrackingMonitoringReportEnabled &&
              reportingId === 'fluids_in'
            ) {
              if (!formattedCellData) {
                return null
              }

              const targetWasSet = cellData?.target

              const targetWasMet =
                cellData?.value &&
                cellData?.target &&
                cellData?.value >= cellData?.target

              const targetIcon = targetWasMet ? <Success /> : <WarningIcon />

              const getTextWeight = () => {
                if (!targetWasSet) {
                  return 'regular'
                }

                if (targetWasMet) {
                  return 'medium'
                }

                return 'bold'
              }

              return (
                <Container>
                  <Text weight={getTextWeight()}>
                    {`${formattedCellData || 0}ml`}
                  </Text>
                  {targetWasSet && targetIcon}
                </Container>
              )
            }

            return <Text>{formattedCellData}</Text>
          }

          const cellChildren = (
            <StyledLink
              rel="noopener noreferrer"
              target="_blank"
              to={`/logs${generateQueryString({
                resident: record.resident.id,
                from: cellData?.date,
                to: cellData?.date,
                loggedBy: filterParams?.loggedBy || [],
                categories: filterParams.categories || [],
                // @ts-ignore
                items: record.items || []
              })}`}
            >
              {getCellValue()}
            </StyledLink>
          )

          return (
            <MonitoringReportCell>
              {cellData && (
                <Popover
                  placement="top"
                  showArrow
                  trigger="hover"
                  content={
                    <MonitoringReportCellPopover
                      cellData={cellData}
                      reportingId={reportingId}
                    />
                  }
                >
                  {cellChildren}
                </Popover>
              )}
              {!cellData && cellChildren}
            </MonitoringReportCell>
          )
        }
      }))
    }))
  ]

  return (
    <StyledTable
      bordered
      dataSource={dataSource}
      columns={columns}
      scroll={{ x: 'max-content' }}
      pagination={{
        showSizeChanger: true,
        position: ['bottomCenter']
      }}
    />
  )
}

const MonitoringReportCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color3};
  width: 40px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.status.primary};
  }
`

const YearContainer = styled.div`
  display: flex;
  margin-bottom: -12px;
`

const YearText = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
  white-space: nowrap;
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const BaseText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DayText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const PersonCard = styled.div`
  width: 220px;
  display: flex;
  align-items: center;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 120px;
  }
`

const SpacedColumnHeader = styled.div`
  margin-left: 8px;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

const StyledTable = styled(Table<MonitoringReportRow>)`
  tr.ant-table-row.ant-table-row-level-0:last-child {
    > td:first-child {
      border-bottom-left-radius: 0px;
    }
    > td:last-child {
      border-bottom-right-radius: 0px;
    }
  }
`

export default DashboardTable
